import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { PongoAssetListBlockConfig } from '@front-platform/content-builder';
import { pongoAssetListBlockResolveConfig } from '@front-platform/content-builder';
import { find } from 'lodash';
import { Store } from '@ngrx/store';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import type { PongoAssetListBlockScenarioContent } from '@front-platform/content-builder';
import { PONGO_ASSET_LIST_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { CartService } from '@core/services/cart/cart.service';
import { PongoService } from '@core/services/pongo/pongo.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { ModalsService } from '@core/services/modals/modals.service';

import { MarketplaceStoreRefiner } from '@common/refiners/marketplace.refiner';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { SELECT_AUTH_USER_META_FIELDS } from '@store/authentication';
import { IPongoAsset, IPongoCustomer, PongoAssetType } from '@core/models/pongo.model';
import { PongoOfferedProductPickerModalComponent } from '@shared/modals/pongo/pongo-offered-product-picker-modal/pongo-offered-product-picker-modal.component';
import { MarketplaceKeys } from '@config/keys/shop.keys';
import { MarketplaceCartScopeValues } from '@config/values/marketplace.values';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ProductPushV2AtomHoverColors } from '@shared/interfaces/product-hover-colors.interface';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';
export interface PongoAssetListBlockRuntimeConfig {}

export type PongoAssetListBlockConfigInput = Partial<
  Omit<PongoAssetListBlockConfig, keyof PongoAssetListBlockRuntimeConfig> &
    PongoAssetListBlockRuntimeConfig
>;
export type PongoAssetListBlockResolvedConfig = Omit<
  PongoAssetListBlockConfig,
  keyof PongoAssetListBlockRuntimeConfig
> &
  Partial<PongoAssetListBlockRuntimeConfig>;
export interface PongoAssetListBlockContract extends ConfigComponentContract<
  PongoAssetListBlockConfigInput,
  PongoAssetListBlockResolvedConfig
> {}

@Component({
  selector: 'app-pongo-asset-list-block',
  templateUrl: './pongo-asset-list-block.component.html',
  styleUrls: ['./pongo-asset-list-block.component.scss'],
  standalone: false,
})
export class PongoAssetListBlockComponent
  extends DestroyerBase
  implements OnInit, AfterViewInit, PongoAssetListBlockContract
{
  readonly config = input<PongoAssetListBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    PongoAssetListBlockConfig,
    PongoAssetListBlockRuntimeConfig
  >(pongoAssetListBlockResolveConfig, () => ({ config: this.config() }));
  get heading(): string {
    return this.resolvedConfig()['heading'] as unknown as string;
  }
  get subheading(): string | undefined {
    return this.resolvedConfig()['subheading'] as unknown as string | undefined;
  }
  get width(): 'centered' | 'full' {
    return this.resolvedConfig()['width'] as unknown as 'centered' | 'full';
  }

  protected readonly defaultBackgroundColor: IContentBuilderFieldColor = {
    value: 'neutral-100',
    type: ColorFieldTypesValues.Palette,
  };

  protected readonly defaultHeadingColor: IContentBuilderFieldColor = {
    value: 'neutral-800',
    type: ColorFieldTypesValues.Palette,
  };

  protected readonly defaultTextColor: IContentBuilderFieldColor = {
    value: 'neutral-600',
    type: ColorFieldTypesValues.Palette,
  };

  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }

  get headingColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor;
  }

  get textColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor;
  }

  get iconColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['icon_color'] as unknown as IContentBuilderFieldColor;
  }

  private readonly defaultProductNameColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  };

  private readonly defaultProductDescriptionColor: IContentBuilderFieldColor = {
    value: 'neutral-600',
    type: ColorFieldTypesValues.Palette,
  };

  private readonly defaultProductImagePlaceholderColor: IContentBuilderFieldColor = {
    value: 'neutral-50',
    type: ColorFieldTypesValues.Palette,
  };

  private readonly defaultProductBorderColor: IContentBuilderFieldColor = {
    value: 'neutral-100',
    type: ColorFieldTypesValues.Palette,
  };

  private readonly defaultProductBackgroundColor: IContentBuilderFieldColor = {
    value: '#FFFFFF',
    type: ColorFieldTypesValues.Hex,
  };

  private readonly defaultHoverBackgroundColor: IContentBuilderFieldColor = {
    value: 'neutral-50',
    type: ColorFieldTypesValues.Palette,
  };

  get productNameColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['product_name_color'] as unknown as IContentBuilderFieldColor;
  }

  get productDescriptionColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'product_description_color'
    ] as unknown as IContentBuilderFieldColor;
  }

  get productImagePlaceholderColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'product_image_placeholder_color'
    ] as unknown as IContentBuilderFieldColor;
  }

  get productBorderColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['product_border_color'] as unknown as IContentBuilderFieldColor;
  }

  get productBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'product_background_color'
    ] as unknown as IContentBuilderFieldColor;
  }

  get productHoverColors(): ProductPushV2AtomHoverColors {
    return this.resolvedConfig()['product_hover_colors'] as unknown as ProductPushV2AtomHoverColors;
  }

  get productNameColorValue(): string {
    return fromContentBuilderColor(this.productNameColor, this.defaultProductNameColor);
  }

  get productDescriptionColorValue(): string {
    return fromContentBuilderColor(
      this.productDescriptionColor,
      this.defaultProductDescriptionColor,
    );
  }

  get productImagePlaceholderColorValue(): string {
    return fromContentBuilderColor(
      this.productImagePlaceholderColor,
      this.defaultProductImagePlaceholderColor,
    );
  }

  get productBorderColorValue(): string {
    return fromContentBuilderColor(this.productBorderColor, this.defaultProductBorderColor);
  }

  get productBackgroundColorValue(): string {
    return fromContentBuilderColor(
      this.productBackgroundColor,
      this.defaultProductBackgroundColor,
    );
  }

  get hoverBackgroundColorValue(): string {
    return fromContentBuilderColor(
      this.productHoverColors?.backgroundColor,
      this.defaultHoverBackgroundColor,
    );
  }

  get hoverNameColorValue(): string {
    return fromContentBuilderColor(
      this.productHoverColors?.nameColor,
      this.productNameColor || this.defaultProductNameColor,
    );
  }

  get hoverDescriptionColorValue(): string {
    return fromContentBuilderColor(
      this.productHoverColors?.descriptionColor,
      this.productDescriptionColor || this.defaultProductDescriptionColor,
    );
  }

  @ViewChild('scrollContainer', { static: false })
  scrollContainerRef?: ElementRef<HTMLDivElement>;

  showDesktopScrollbar = false;
  thumbWidthPercent = 100;
  thumbLeftPercent = 0;

  private readonly ngZone = inject(NgZone);
  private readonly desktopMedia =
    typeof window === 'undefined' ? null : window.matchMedia('(min-width: 993px)');
  private readonly resizeObserver = new ResizeObserver(() => {
    this.ngZone.run(() => this.updateScrollbar());
  });
  private observedRow?: HTMLElement;
  private scrollbarDragging = false;

  customer?: IPongoCustomer;
  assets: IPongoAsset[] = [];
  /** Preview marketplaces often have no Pongo plugin; scenarios still need the list. */
  previewForceVisible = false;

  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewScenario$ = previewScenarioForBloc$(PONGO_ASSET_LIST_BLOCK_COMPONENT);
  selectIsPongoPluginEnabled = this.marketplaceRefiner.selectIsPongoPluginEnabledSignal;

  constructor(
    private store: Store,
    private readonly cartService: CartService,
    private readonly pongoService: PongoService,
    private readonly ordersService: OrdersService,
    private readonly modalsService: ModalsService,
    private readonly marketplaceRefiner: MarketplaceStoreRefiner,
    private readonly shopSelector: ShopStoreSelector,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
  ) {
    super();
  }

  @ViewChild('assetsRow')
  set assetsRowRef(ref: ElementRef<HTMLElement> | undefined) {
    if (this.observedRow) {
      this.resizeObserver.unobserve(this.observedRow);
      this.observedRow = undefined;
    }
    if (ref) {
      this.observedRow = ref.nativeElement;
      this.resizeObserver.observe(ref.nativeElement);
    }
    this.updateScrollbar();
  }

  ngAfterViewInit(): void {
    const el = this.scrollContainerRef?.nativeElement;
    if (!el) {
      return;
    }
    this.resizeObserver.observe(el);
    this.desktopMedia?.addEventListener('change', this.onDesktopChange);
    el.addEventListener(
      'wheel',
      (event: WheelEvent) => {
        if (event.deltaY !== 0) {
          event.preventDefault();
          el.scrollLeft += event.deltaY;
        }
      },
      { passive: false },
    );
    this.updateScrollbar();
  }

  override ngOnDestroy(): void {
    this.resizeObserver.disconnect();
    this.desktopMedia?.removeEventListener('change', this.onDesktopChange);
    super.ngOnDestroy();
  }

  onAssetsScroll(): void {
    if (!this.scrollbarDragging) {
      this.updateScrollbar();
    }
  }

  onScrollbarPointerDown(event: PointerEvent): void {
    const track = event.currentTarget as HTMLElement;
    track.setPointerCapture(event.pointerId);
    this.scrollbarDragging = true;
    this.scrollFromPointer(event, track);
  }

  onScrollbarPointerMove(event: PointerEvent): void {
    if (!this.scrollbarDragging) {
      return;
    }
    this.scrollFromPointer(event, event.currentTarget as HTMLElement);
  }

  onScrollbarPointerUp(): void {
    this.scrollbarDragging = false;
    this.updateScrollbar();
  }

  private readonly onDesktopChange = (): void => {
    this.ngZone.run(() => this.updateScrollbar());
  };

  private updateScrollbar(): void {
    const el = this.scrollContainerRef?.nativeElement;
    if (!el) {
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    const overflows = maxScroll > 1;
    this.showDesktopScrollbar = !!this.desktopMedia?.matches && overflows;
    if (!overflows) {
      this.thumbWidthPercent = 100;
      this.thumbLeftPercent = 0;
      return;
    }
    const rawWidth = (el.clientWidth / el.scrollWidth) * 100;
    this.thumbWidthPercent = Math.min(100, Math.max(rawWidth, 12));
    const travel = 100 - this.thumbWidthPercent;
    this.thumbLeftPercent = travel * (el.scrollLeft / maxScroll);
  }

  private scrollFromPointer(event: PointerEvent, track: HTMLElement): void {
    const el = this.scrollContainerRef?.nativeElement;
    if (!el) {
      return;
    }
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const maxScroll = el.scrollWidth - el.clientWidth;
    const thumbRatio = this.thumbWidthPercent / 100;
    el.scrollLeft = ((ratio - thumbRatio / 2) / (1 - thumbRatio)) * maxScroll;
    this.updateScrollbar();
  }

  ngOnInit(): void {
    if (this.cb2PreviewState.isActive()) {
      this.previewForceVisible = true;
      this.previewScenario$
        .pipe(
          takeUntil(this._destroyerRef),
          tap(scenario => {
            const content = previewScenarioData<PongoAssetListBlockScenarioContent>(
              scenario,
              PONGO_ASSET_LIST_BLOCK_COMPONENT,
            );
            const customer = content?.customer;
            if (!customer) {
              this.customer = undefined;
              this.assets = [];
              return;
            }
            this.customer = {
              points: customer.points,
              rewards: customer.rewards as unknown as IPongoAsset[],
            };
            this.assets = this.customer.rewards.filter(reward => {
              return reward.type !== PongoAssetType.FREE_PRODUCT || reward.external_ids.length > 0;
            });
          }),
        )
        .subscribe();
      return;
    }

    this.updateCustomer();
  }

  private updateCustomer(): void {
    const marketplace = this.marketplaceSelector.marketplace;

    this.store
      .select(SELECT_AUTH_USER_META_FIELDS)
      .pipe(
        takeUntil(this._destroyerRef),
        switchMap(() => this.shopSelector.selectActive$),
        filter(shop => {
          return (
            marketplace[MarketplaceKeys.CartScope] === MarketplaceCartScopeValues.Marketplace ||
            !!shop
          );
        }),
        switchMap(shop => {
          const isCartScopeShop =
            marketplace[MarketplaceKeys.CartScope] === MarketplaceCartScopeValues.Shop;
          return this.pongoService.getCustomer$(isCartScopeShop ? shop : null);
        }),
        tap(customer => {
          if (!customer) return;
          this.customer = customer;
          this.assets = customer.rewards.filter(reward => {
            let hasExternalIds =
              reward.type !== PongoAssetType.FREE_PRODUCT || reward.external_ids.length > 0;
            return hasExternalIds;
          });
        }),
      )
      .subscribe();
  }

  useAsset(asset: IPongoAsset): void {
    const coupon = { code: asset.hash };
    this.cartService.addCoupon(coupon);

    if (asset.external_ids.length > 0) {
      PongoOfferedProductPickerModalComponent.asset = asset;
      this.updateCart();

      this.modalsService.open(PongoOfferedProductPickerModalComponent.handle);
    } else {
      this.updateCart();
    }
  }

  removeAsset(asset: IPongoAsset): void {
    const coupon = { code: asset.hash };
    this.cartService.removeCoupon(coupon);
    this.updateCart();
  }

  buyAsset(asset: IPongoAsset): void {
    asset.buyInProcess = true;
    this.pongoService
      .buyReward$(asset.id)
      .pipe(
        tap(() => (asset.buyInProcess = false)),
        tap(() => this.updateCustomer()),
        tap(asset => this.useAsset(asset)),
      )
      .subscribe();
  }

  isAssetInCart(asset: IPongoAsset): boolean {
    const coupons = this.cartService.getCouponsInCart();

    return !!find(coupons, coupon => coupon.code && coupon.code === asset.hash);
  }

  private updateCart(): void {
    this.ordersService
      .checkCartIsValid$()
      .pipe(
        take(1),
        tap(() => {
          window.scroll({
            top: 0,
            behavior: 'smooth',
          });
        }),
      )
      .subscribe();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(PongoAssetListBlockComponent, { 'keyAliases': {} });
